/**
 * Sincronizar la agenda con Google Calendar, Outlook o Apple.
 *
 * Pensada para quien no sabe qué es iCal. Todo empieza por elegir SU calendario
 * —el logo que reconoce— y a partir de ahí:
 *
 * - «Ver mis citas en mi calendario» es un solo clic: el enlace se crea al
 *   pulsar y se abre ya suscrito en ese calendario. Copiarlo a mano queda como
 *   opción, no como el camino.
 * - «No ofrecer horarios cuando estoy ocupado» son tres pasos numerados para
 *   ese calendario, con un botón que abre la pantalla exacta de sus ajustes y un
 *   campo para pegar. Nada de «dirección secreta en formato iCal»: se habla de
 *   «el enlace privado», y si se pega el enlace equivocado —el de la página del
 *   calendario, que es el que todo el mundo copia primero— se explica cuál es.
 *
 * Sin permisos de Google ni de Microsoft: ver `services/calendarSync.ts`.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  IconAlertTriangle,
  IconBrandApple,
  IconBrandWindows,
  IconCalendarEvent,
  IconCalendarOff,
  IconCalendarShare,
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconExternalLink,
  IconLoader2,
  IconLock,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconX,
} from '@tabler/icons-react'
import {
  addCalendarFeed,
  disconnectGoogleCalendar,
  startGoogleCalendar,
  updateGoogleCalendar,
  createCalendarExport,
  deleteCalendarExport,
  getCalendarSync,
  removeCalendarFeed,
  syncCalendarFeed,
  type BookingResource,
  type CalendarFeed,
  type CalendarSyncState,
  type GoogleCalendarAccount,
  WORKER_URL,
} from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'
import { useToast } from '../../Toast'
import { ConfirmDialog } from '../ConfirmDialog'
import { errorMessage, failureDetail } from './offerShared'
import { GoogleCalendarLogo } from './GoogleCalendarMark'

type Provider = 'google' | 'outlook' | 'apple'

function GoogleGLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z" fill="#4285F4" />
      <path d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" fill="#34A853" />
      <path d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z" fill="#FBBC05" />
      <path d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" fill="#EA4335" />
    </svg>
  )
}

/**
 * Los que van por enlace.
 *
 * Google no está: con su cuenta conectada hace lo mismo y mejor, y ofrecer las
 * dos formas del mismo calendario a la vez sólo hace dudar de cuál es la buena.
 * Quien no quiera conectarla puede pegar igual su enlace de Google —se acepta—
 * pero no se ofrece aquí.
 */
const OTHER_PROVIDERS: { id: Exclude<Provider, 'google'>; Icon: typeof IconBrandWindows }[] = [
  { id: 'outlook', Icon: IconBrandWindows },
  { id: 'apple', Icon: IconBrandApple },
]

/** Dónde se saca el enlace privado de cada calendario. */
const SETTINGS_URL: Record<Provider, string> = {
  google: 'https://calendar.google.com/calendar/r/settings',
  outlook: 'https://outlook.live.com/calendar/0/options/calendar/SharedCalendars',
  apple: 'https://www.icloud.com/calendar/',
}

/** Lo que le pasa a una cuenta de Google conectada, dicho para el negocio. */
const GOOGLE_ERROR_KEY: Record<string, string> = {
  reconnect: 'calsync.google.error.reconnect',
  forbidden: 'calsync.google.error.forbidden',
  unreachable: 'calsync.google.error.unreachable',
}

const FEED_ERROR_KEY: Record<string, string> = {
  'not-ical': 'calsync.error.notIcal',
  'not-found': 'calsync.error.notFound',
  timeout: 'calsync.error.timeout',
  'too-large': 'calsync.error.tooLarge',
  unreachable: 'calsync.error.unreachable',
  secret: 'calsync.error.secret',
}

/**
 * Si el panel habla con un worker local.
 *
 * Google, Outlook y Apple leen el enlace desde SUS servidores, que no llegan a
 * `localhost`: suscribirse en local siempre termina en «no se pudo suscribir».
 * Se dice antes de pulsar en vez de mandar a nadie a ese error.
 */
const LOCAL_WORKER = /^https?:\/\/(localhost|127\.|0\.0\.0\.0|\[::1\])/i.test(WORKER_URL)

/** `https://…` → `webcal://…`, que abre la app de calendario del sistema. */
function webcalOf(url: string): string {
  return url.replace(/^https?:\/\//i, 'webcal://')
}

/** A dónde mandar el enlace de las citas para que quede suscrito en cada calendario. */
function subscribeUrl(provider: Provider, exportUrl: string, calendarName: string): string {
  if (provider === 'google') return `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalOf(exportUrl))}`
  if (provider === 'outlook') {
    return `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(exportUrl)}&name=${encodeURIComponent(calendarName)}`
  }
  return webcalOf(exportUrl)
}

/**
 * Si lo pegado es el enlace de la página del calendario y no el privado.
 *
 * Es el error de casi todo el mundo: se copia la barra de direcciones. Se avisa
 * antes de mandarlo, con qué buscar en su lugar.
 */
function looksLikePageLink(raw: string): boolean {
  const value = raw.trim().toLowerCase()
  if (!/^(https?|webcals?):\/\//.test(value)) return false
  if (value.includes('calendar.google.com')) return !value.includes('/ical/')
  if (value.includes('outlook.live.com') || value.includes('outlook.office.com')) return !/\.ics(\?|$)/.test(value)
  return false
}

/** El calendario de un enlace pegado, para nombrarlo solo. */
function providerOfHost(host: string): Provider | null {
  if (host.includes('google')) return 'google'
  if (host.includes('outlook') || host.includes('office')) return 'outlook'
  if (host.includes('icloud') || host.includes('apple')) return 'apple'
  return null
}

export function CalendarSyncDialog({ projectId, token, resources, onClose }: {
  projectId: string
  token: string
  resources: BookingResource[]
  onClose: () => void
}) {
  const { t, fmt } = useI18n()
  const { showToast } = useToast()
  const [state, setState] = useState<CalendarSyncState | null>(null)
  const [loadFailed, setLoadFailed] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [provider, setProvider] = useState<Exclude<Provider, 'google'>>('outlook')
  /* Plegados, y abiertos solos si ya hay algo conectado por enlace: ahí dentro
     está el estado de esos calendarios. */
  const [otherOpen, setOtherOpen] = useState(false)
  const [blockOpen, setBlockOpen] = useState(false)
  const [url, setUrl] = useState('')
  const [resourceId, setResourceId] = useState('')
  /* La agenda de la cuenta de Google, aparte de la del formulario de iCal: el
     selector de aquél vive al final de la ventana, y usarlo para esto conectaba
     la cuenta a una agenda que quien pulsa no ha visto elegir. */
  const [googleResourceId, setGoogleResourceId] = useState('')
  const [addError, setAddError] = useState('')
  const [confirm, setConfirm] = useState<
    | { kind: 'rotate' }
    | { kind: 'stop' }
    | { kind: 'remove'; feed: CalendarFeed }
    | { kind: 'google'; account: GoogleCalendarAccount }
    | null
  >(null)

  const load = useCallback(async () => {
    try {
      setState(await getCalendarSync(projectId, token))
      setLoadFailed('')
    } catch (error) {
      console.error('[calendario] no se pudo cargar la sincronización', error)
      /* El motivo técnico, además de la frase: aquí el fallo más probable es una
         migración sin aplicar, y «inténtalo de nuevo» manda a reintentar algo
         que no se arregla reintentando. Va en pequeño y se puede copiar. */
      setLoadFailed(failureDetail(error) || 'error')
    }
  }, [projectId, token])

  useEffect(() => { void load() }, [load])

  /* Con calendarios por enlace ya conectados, la zona se abre sola: si no, su
     estado —y su posible error— quedaría detrás de un botón cerrado. */
  useEffect(() => {
    if (state && (state.feeds.length > 0 || !state.googleReady)) setOtherOpen(true)
  }, [state])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !confirm) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, confirm])

  const providerName = (id: Provider) => t(`calsync.provider.${id}`)
  const exportUrl = state?.exportUrl ?? null

  /**
   * Un clic: crea el enlace si hace falta y lo abre suscrito.
   *
   * La pestaña se abre ANTES de pedir el enlace: abrirla después de esperar al
   * servidor la convierte en una ventana emergente que el navegador bloquea.
   */
  const addToCalendar = async (target: Provider) => {
    const tab = target === 'apple' ? null : window.open('', '_blank')
    setBusy(`export:${target}`)
    try {
      let link = exportUrl
      if (!link) {
        link = (await createCalendarExport(projectId, token)).exportUrl
        setState((current) => current ? { ...current, exportUrl: link } : current)
      }
      const destination = subscribeUrl(target, link, t('calsync.export.calendarName'))
      if (tab) tab.location.href = destination
      else window.location.href = destination
    } catch (error) {
      tab?.close()
      showToast(errorMessage(error), 'error')
    } finally {
      setBusy(null)
    }
  }

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key)
    try {
      await action()
    } catch (error) {
      showToast(errorMessage(error), 'error')
    } finally {
      setBusy(null)
    }
  }

  const connect = async () => {
    const value = url.trim()
    if (!value) return
    if (looksLikePageLink(value)) {
      setAddError(t(`calsync.import.wrongLink.${provider}`))
      return
    }
    setAddError('')
    setBusy('add')
    try {
      const host = (() => { try { return new URL(value.replace(/^webcals?:/i, 'https:')).hostname } catch { return '' } })()
      const detected = providerOfHost(host)
      const feed = await addCalendarFeed(projectId, {
        name: detected ? providerName(detected) : '',
        url: value,
        resourceId: resourceId || null,
      }, token)
      setState((current) => current ? { ...current, feeds: [...current.feeds, feed] } : current)
      setUrl('')
      if (feed.lastError) {
        setAddError(t(FEED_ERROR_KEY[feed.lastError] ?? 'calsync.error.unreachable'))
      } else {
        showToast(t('calsync.toast.added', { count: feed.busyCount }))
      }
    } catch (error) {
      setAddError(errorMessage(error))
    } finally {
      setBusy(null)
    }
  }

  const feedStatus = (feed: CalendarFeed) => {
    if (feed.lastError) return t(FEED_ERROR_KEY[feed.lastError] ?? 'calsync.error.unreachable')
    if (!feed.lastSyncedAt) return t('calsync.feed.pending')
    return t('calsync.feed.synced', { when: fmt.relative(feed.lastSyncedAt) ?? '', count: feed.busyCount })
  }

  const feedTitle = (feed: CalendarFeed) => {
    const detected = providerOfHost(feed.host)
    return feed.name || (detected ? providerName(detected) : feed.host)
  }

  return (
    <div className="dash-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !confirm) onClose() }}>
      <section className="dash-modal dash-coupon-modal dash-calsync-modal" role="dialog" aria-modal="true" aria-label={t('calsync.title')}>
        <header className="dash-coupon-modal-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconCalendarShare size={18} /></span>
          <div>
            <h2>{t('calsync.title')}</h2>
            <p>{t('calsync.lead')}</p>
          </div>
          <button type="button" className="dash-icon-btn" aria-label={t('calsync.close')} onClick={onClose}><IconX size={16} /></button>
        </header>

        <div className="dash-coupon-modal-body">
          {loadFailed ? (
            <div className="dash-calsync-alert" role="alert">
              <IconAlertTriangle size={16} aria-hidden="true" />
              <span>
                {t('calsync.loadFailed')}
                {loadFailed === 'error' ? null : <code className="dash-failure-detail">{loadFailed}</code>}
              </span>
              <button type="button" className="dash-secondary-btn" onClick={() => { void load() }}>{t('calsync.retry')}</button>
            </div>
          ) : null}

          {/*
            Google primero y solo.

            Hace las dos cosas —escribir las citas y bloquear horarios— con un
            clic, así que es LA opción. Todo lo demás existe para quien no puede
            o no quiere conectar su cuenta, y por eso va plegado: esta ventana
            llegó a enseñar los dos sentidos, tres proveedores y tres pasos a la
            vez, y así no se lee ninguno.
          */}
          {/*
            Google primero y solo.

            Hace las dos cosas —escribir las citas y bloquear horarios— con un
            clic, así que es LA opción. Cada cuenta es una ficha con sus dos
            interruptores DENTRO: sueltos debajo no se sabía de qué cuenta eran.
            El botón grande sólo antes de conectar; después, conectar otra es un
            gesto secundario y va discreto.
          */}
          {state?.googleReady ? (
            <section className="dash-calsync-card">
              <header className="dash-calsync-card-head">
                <span className="dash-calsync-logo" aria-hidden="true"><GoogleCalendarLogo size={26} /></span>
                <span className="dash-calsync-card-copy">
                  <span className="dash-calsync-card-title">
                    {t('calsync.google.title')}
                    <em className="dash-calsync-pill">{t('calsync.google.recommended')}</em>
                  </span>
                  <small>{t('calsync.google.hint')}</small>
                </span>
              </header>

              {state.googleAccounts.map((account) => (
                <div key={account.id} className={`dash-calsync-account${account.lastError ? ' is-error' : ''}`}>
                  <div className="dash-calsync-account-head">
                    <span className="dash-calsync-account-avatar" aria-hidden="true">
                      {account.email ? account.email.charAt(0).toUpperCase() : <GoogleGLogo size={16} />}
                    </span>
                    <span className="dash-calsync-account-copy">
                      <strong>
                        {account.email || t('calsync.google.connected')}
                        {resources.length > 1 ? (
                          <em> · {account.resourceId ? resources.find((resource) => resource.id === account.resourceId)?.name ?? '' : t('calsync.feed.allSchedules')}</em>
                        ) : null}
                      </strong>
                      <small className="dash-calsync-account-status">
                        <i aria-hidden="true" />
                        {account.lastError
                          ? t(GOOGLE_ERROR_KEY[account.lastError] ?? 'calsync.google.error.unreachable')
                          : t('calsync.google.ok')}
                      </small>
                    </span>
                    <button
                      type="button"
                      className="dash-icon-btn"
                      aria-label={t('calsync.google.removeAria', { email: account.email })}
                      data-tooltip={t('calsync.google.remove')}
                      disabled={busy !== null}
                      onClick={() => setConfirm({ kind: 'google', account })}
                    >
                      <IconTrash size={15} />
                    </button>
                  </div>
                  {/* Qué hace la cuenta. Las dos por defecto, separables: hay
                      quien quiere ver las citas y no bloquear su agenda. */}
                  <div className="dash-calsync-account-options">
                    {([
                      ['writeEvents', 'calsync.google.writeLabel', 'calsync.google.writeHint'],
                      ['blockBusy', 'calsync.google.blockLabel', 'calsync.google.blockHint'],
                    ] as const).map(([field, labelKey, hintKey]) => (
                      <div key={field} className="dash-calsync-switch-row">
                        <span>
                          <strong>{t(labelKey)}</strong>
                          <small>{t(hintKey)}</small>
                        </span>
                        <button
                          type="button"
                          className={`dash-page-settings-switch${account[field] ? ' is-on' : ''}`}
                          aria-pressed={account[field]}
                          aria-label={t(labelKey)}
                          disabled={busy !== null}
                          onClick={() => {
                            const next = { writeEvents: account.writeEvents, blockBusy: account.blockBusy, [field]: !account[field] }
                            void run(`google:${account.id}`, async () => {
                              const saved = await updateGoogleCalendar(projectId, account.id, next, token)
                              setState((current) => current
                                ? { ...current, googleAccounts: current.googleAccounts.map((item) => item.id === saved.id ? saved : item) }
                                : current)
                            })
                          }}
                        >
                          <i />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {resources.length > 1 ? (
                <label className="dash-calsync-schedule">
                  <span>{t('calsync.google.schedule')}</span>
                  <select value={googleResourceId} disabled={busy !== null} onChange={(event) => setGoogleResourceId(event.target.value)}>
                    <option value="">{t('calsync.feed.allSchedules')}</option>
                    {resources.map((resource) => <option key={resource.id} value={resource.id}>{resource.name}</option>)}
                  </select>
                </label>
              ) : null}

              <div className="dash-calsync-card-actions">
                <button
                  type="button"
                  className={state.googleAccounts.length > 0 ? 'dash-calsync-add-account' : 'dash-calsync-google-btn'}
                  disabled={busy !== null}
                  onClick={() => {
                    void run('google-start', async () => {
                      const { url } = await startGoogleCalendar(projectId, googleResourceId || null, token)
                      window.location.href = url
                    })
                  }}
                >
                  {busy === 'google-start'
                    ? <IconLoader2 size={16} className="dash-spin" />
                    : state.googleAccounts.length > 0 ? <IconPlus size={15} /> : <GoogleGLogo size={18} />}
                  <span>{state.googleAccounts.length > 0 ? t('calsync.google.addAnother') : t('calsync.google.connect')}</span>
                </button>
                {state.googleAccounts.length === 0 ? (
                  <p className="dash-calsync-google-permissions">{t('calsync.google.permissions')}</p>
                ) : null}
              </div>
            </section>
          ) : null}

          {/* ── Los demás calendarios, plegados ──
              Dentro, los dos sentidos por separado y con su título: ver las
              citas en ese calendario, y usarlo para no ofrecer horarios. Antes
              iban seguidos y no se sabía dónde acababa uno y empezaba el otro. */}
          <section className="dash-calsync-card">
            <button
              type="button"
              className="dash-calsync-card-head is-toggle"
              aria-expanded={otherOpen}
              onClick={() => setOtherOpen((open) => !open)}
            >
              <span className="dash-calsync-logo is-muted" aria-hidden="true"><IconCalendarEvent size={20} /></span>
              <span className="dash-calsync-card-copy">
                <span className="dash-calsync-card-title">{t('calsync.other.title')}</span>
                <small>{t('calsync.other.hint')}</small>
              </span>
              <IconChevronDown size={16} className={`dash-calsync-chevron${otherOpen ? ' is-open' : ''}`} aria-hidden="true" />
            </button>

            {otherOpen ? (
              <div className="dash-calsync-card-body">
                <div className="dash-calsync-tabs" role="tablist" aria-label={t('calsync.import.pickAria')}>
                  {OTHER_PROVIDERS.map(({ id, Icon }) => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={provider === id}
                      className={provider === id ? 'is-on' : ''}
                      onClick={() => { setProvider(id); setAddError('') }}
                    >
                      <Icon size={15} /> {providerName(id)}
                    </button>
                  ))}
                </div>

                {LOCAL_WORKER ? (
                  <p className="dash-calsync-local-note">
                    <IconAlertTriangle size={14} aria-hidden="true" />
                    <span>{t('calsync.export.localNote')}</span>
                  </p>
                ) : null}

                {/* Ver las citas en ese calendario: un clic. */}
                <div className="dash-calsync-sub">
                  <div className="dash-calsync-sub-head">
                    <span className="dash-calsync-sub-icon" aria-hidden="true"><IconCalendarShare size={16} /></span>
                    <span className="dash-calsync-card-copy">
                      <strong>{t('calsync.export.titleFor', { provider: providerName(provider) })}</strong>
                      <small>{t('calsync.export.hint')}</small>
                    </span>
                  </div>
                  <div className="dash-calsync-other-row">
                    <button
                      type="button"
                      className="dash-primary-btn"
                      disabled={!state || busy !== null || LOCAL_WORKER}
                      onClick={() => { void addToCalendar(provider) }}
                    >
                      {busy === `export:${provider}` ? <IconLoader2 size={15} className="dash-spin" /> : <IconCalendarShare size={15} />}
                      {t('calsync.export.addTo', { provider: providerName(provider) })}
                    </button>
                    {exportUrl ? (
                      <button
                        type="button"
                        className="dash-secondary-btn"
                        onClick={() => { void navigator.clipboard?.writeText(exportUrl); showToast(t('calsync.toast.copied')) }}
                      >
                        <IconCopy size={14} /> <span>{t('calsync.copy')}</span>
                      </button>
                    ) : LOCAL_WORKER && state ? (
                      <button
                        type="button"
                        className="dash-calsync-link"
                        disabled={busy !== null}
                        onClick={() => {
                          void run('export', async () => {
                            const { exportUrl: next } = await createCalendarExport(projectId, token)
                            setState((current) => current ? { ...current, exportUrl: next } : current)
                          })
                        }}
                      >
                        {t('calsync.export.createLocal')}
                      </button>
                    ) : null}
                  </div>
                  {exportUrl ? (
                    <div className="dash-calsync-export-extra">
                      <small><IconLock size={13} aria-hidden="true" /> {t('calsync.export.delay')}</small>
                      <div className="dash-calsync-actions">
                        <button type="button" className="dash-calsync-action-link" disabled={busy !== null} onClick={() => setConfirm({ kind: 'rotate' })}>{t('calsync.export.rotate')}</button>
                        <button type="button" className="dash-calsync-action-link is-danger" disabled={busy !== null} onClick={() => setConfirm({ kind: 'stop' })}>{t('calsync.export.stop')}</button>
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Y bloquear horarios con él: tres pasos, detrás de su propio
                    botón. Es lo más largo de toda la ventana y lo que menos
                    gente necesita. */}
                <div className="dash-calsync-sub">
                  <button
                    type="button"
                    className="dash-calsync-sub-head is-toggle"
                    aria-expanded={blockOpen}
                    onClick={() => setBlockOpen((open) => !open)}
                  >
                    <span className="dash-calsync-sub-icon" aria-hidden="true"><IconCalendarOff size={16} /></span>
                    <span className="dash-calsync-card-copy">
                      <strong>{t('calsync.import.title')}</strong>
                      <small>{t('calsync.import.hint')}</small>
                    </span>
                    <IconChevronDown size={15} className={`dash-calsync-chevron${blockOpen ? ' is-open' : ''}`} aria-hidden="true" />
                  </button>

                  {blockOpen ? (
                    <div className="dash-calsync-guide">
                      <ol className="dash-calsync-steps">
                        <li>
                          <span>{t(`calsync.guide.${provider}.one`)}</span>
                          <a className="dash-secondary-btn dash-calsync-open" href={SETTINGS_URL[provider]} target="_blank" rel="noreferrer">
                            {t('calsync.guide.open', { provider: providerName(provider) })} <IconExternalLink size={13} />
                          </a>
                        </li>
                        <li><span>{t(`calsync.guide.${provider}.two`)}</span></li>
                        <li><span>{t(`calsync.guide.${provider}.three`)}</span></li>
                      </ol>

                      <div className="dash-calsync-paste">
                        <input
                          value={url}
                          placeholder={t('calsync.import.pastePlaceholder')}
                          aria-label={t('calsync.import.pastePlaceholder')}
                          autoComplete="off"
                          spellCheck={false}
                          onChange={(event) => { setUrl(event.target.value); setAddError('') }}
                          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void connect() } }}
                        />
                        <button type="button" className="dash-primary-btn" disabled={busy !== null || !url.trim() || !state} onClick={() => { void connect() }}>
                          {busy === 'add' ? <IconLoader2 size={15} className="dash-spin" /> : null}
                          {t('calsync.import.connect')}
                        </button>
                      </div>
                      {resources.length > 1 ? (
                        <label className="dash-calsync-schedule">
                          <span>{t('calsync.import.schedule')}</span>
                          <select value={resourceId} onChange={(event) => setResourceId(event.target.value)}>
                            <option value="">{t('calsync.feed.allSchedules')}</option>
                            {resources.map((resource) => <option key={resource.id} value={resource.id}>{resource.name}</option>)}
                          </select>
                        </label>
                      ) : null}
                      {addError ? <p className="dash-calsync-error" role="alert"><IconAlertTriangle size={14} aria-hidden="true" /> {addError}</p> : null}
                    </div>
                  ) : null}
                </div>

                {/* Los ya conectados, siempre a la vista dentro de esta zona:
                    es donde se mira si siguen funcionando. */}
                {state && state.feeds.length > 0 ? (
                  <ul className="dash-calsync-feeds">
                    {state.feeds.map((feed) => (
                      <li key={feed.id} className={feed.lastError ? 'is-error' : ''}>
                        <span className="dash-calsync-feed-icon" aria-hidden="true">
                          {feed.lastError ? <IconAlertTriangle size={15} /> : <IconCheck size={15} />}
                        </span>
                        <span className="dash-calsync-feed-copy">
                          <strong>
                            {feedTitle(feed)}
                            {resources.length > 1 ? (
                              <em> · {feed.resourceId ? resources.find((resource) => resource.id === feed.resourceId)?.name ?? '' : t('calsync.feed.allSchedules')}</em>
                            ) : null}
                          </strong>
                          <small className="dash-calsync-feed-status">{feedStatus(feed)}</small>
                        </span>
                        <button
                          type="button"
                          className="dash-icon-btn"
                          aria-label={t('calsync.feed.syncAria', { name: feedTitle(feed) })}
                          data-tooltip={t('calsync.feed.sync')}
                          disabled={busy !== null}
                          onClick={() => {
                            void run(`sync:${feed.id}`, async () => {
                              const next = await syncCalendarFeed(projectId, feed.id, token)
                              setState((current) => current ? { ...current, feeds: current.feeds.map((item) => item.id === next.id ? next : item) } : current)
                            })
                          }}
                        >
                          <IconRefresh size={15} className={busy === `sync:${feed.id}` ? 'dash-spin' : undefined} />
                        </button>
                        <button
                          type="button"
                          className="dash-icon-btn"
                          aria-label={t('calsync.feed.removeAria', { name: feedTitle(feed) })}
                          data-tooltip={t('calsync.feed.remove')}
                          disabled={busy !== null}
                          onClick={() => setConfirm({ kind: 'remove', feed })}
                        >
                          <IconTrash size={15} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </section>
        </div>

        <footer>
          <button type="button" className="dash-secondary-btn" onClick={onClose}>{t('calsync.done')}</button>
        </footer>
      </section>

      {confirm?.kind === 'rotate' ? (
        <ConfirmDialog
          title={t('calsync.rotate.title')}
          message={t('calsync.rotate.body')}
          confirmLabel={t('calsync.rotate.confirm')}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            setConfirm(null)
            await run('rotate', async () => {
              const { exportUrl: next } = await createCalendarExport(projectId, token)
              setState((current) => current ? { ...current, exportUrl: next } : current)
            })
          }}
        />
      ) : null}
      {confirm?.kind === 'stop' ? (
        <ConfirmDialog
          title={t('calsync.stop.title')}
          message={t('calsync.stop.body')}
          confirmLabel={t('calsync.stop.confirm')}
          tone="danger"
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            setConfirm(null)
            await run('stop', async () => {
              await deleteCalendarExport(projectId, token)
              setState((current) => current ? { ...current, exportUrl: null } : current)
            })
          }}
        />
      ) : null}
      {confirm?.kind === 'google' ? (
        <ConfirmDialog
          title={t('calsync.google.removeTitle')}
          message={t('calsync.google.removeBody')}
          confirmLabel={t('calsync.google.remove')}
          tone="danger"
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            const account = confirm.account
            setConfirm(null)
            await run(`google-remove:${account.id}`, async () => {
              await disconnectGoogleCalendar(projectId, account.id, token)
              setState((current) => current
                ? { ...current, googleAccounts: current.googleAccounts.filter((item) => item.id !== account.id) }
                : current)
            })
          }}
        />
      ) : null}
      {confirm?.kind === 'remove' ? (
        <ConfirmDialog
          title={t('calsync.remove.title', { name: feedTitle(confirm.feed) })}
          message={t('calsync.remove.body')}
          confirmLabel={t('calsync.remove.confirm')}
          tone="danger"
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            const feed = confirm.feed
            setConfirm(null)
            await run(`remove:${feed.id}`, async () => {
              await removeCalendarFeed(projectId, feed.id, token)
              setState((current) => current ? { ...current, feeds: current.feeds.filter((item) => item.id !== feed.id) } : current)
            })
          }}
        />
      ) : null}
    </div>
  )
}
